import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft } from "lucide-react";

const TITLES = { baseline: "Baseline Review", week6: "Week 6 Foundation Review", week12: "Week 12 Foundation Review" };
const FIELDS = [
  ["goal", "What do you want your body to still be able to do in 10–20 years?", "textarea"],
  ["walking_baseline", "Typical daily walking right now (steps or minutes)", "text"],
  ["session_consistency", "Lifting sessions most weeks (e.g. 2 of 3)", "text"],
  ["strength_benchmark", "Optional strength benchmark (lift · weight · reps)", "text"],
  ["body_weight_kg", "Optional body weight (kg)", "number"],
  ["waist_cm", "Optional waist (cm)", "number"],
  ["reflection", "Reflection — what's working, what to adjust", "textarea"],
];

function Scale({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</label>
      <div className="mt-2 flex gap-2" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)} aria-label={`${label} ${v}`}
            className={`w-11 h-11 rounded-md border text-sm font-head ${value === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{v}</button>
        ))}
      </div>
    </div>
  );
}

export default function FoundationReview() {
  const { stage } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/reviews").then((r) => setForm((prev) => ({ ...(r.data[stage] || {}), ...prev }))).catch(() => {}); }, [stage]);
  if (!TITLES[stage]) return <div className="text-sm text-muted-foreground">Unknown review.</div>;

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== "" && v != null && !["id", "user_id", "stage", "created_at", "updated_at"].includes(k))
      .map(([k, v]) => [k, ["body_weight_kg", "waist_cm"].includes(k) ? Number(v) : v]));
    try {
      await api.post(`/reviews/${stage}`, payload);
      toast.success("Review saved.");
      nav(stage === "week12" ? "/app" : "/app/track");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-2xl" data-testid={`review-${stage}`}>
      <Link to="/app" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-primary"><ChevronLeft size={14} /> Dashboard</Link>
      <div>
        <div className="label-eyebrow">Foundation review</div>
        <h1 className="wordmark mt-2 text-4xl uppercase">{TITLES[stage]}</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-xl">Every field is optional. This is a reflection on habits and capability — not a medical assessment, and nothing here changes your score.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <Scale label="Energy (1 low – 5 high)" value={form.energy} onChange={(v) => setForm({ ...form, energy: v })} testId="review-energy" />
        <Scale label="Everyday capability (1 – 5)" value={form.capability} onChange={(v) => setForm({ ...form, capability: v })} testId="review-capability" />
      </div>
      {FIELDS.map(([k, label, type]) => (
        <div key={k}>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</label>
          {type === "textarea"
            ? <textarea rows={3} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid={`review-${k}`} />
            : <input type={type} step="0.1" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[44px]" data-testid={`review-${k}`} />}
        </div>
      ))}
      <p className="text-xs text-muted-foreground">General educational guidance only. If you have a medical condition, injury or new symptoms, please seek advice from a qualified health professional.</p>
      <button type="submit" disabled={saving} className="btn-primary w-full min-h-[44px] disabled:opacity-50" data-testid="review-submit">{saving ? "Saving…" : "Save review"}</button>
    </form>
  );
}
